/* eslint-disable import/no-unresolved,jsx-a11y/control-has-associated-label */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { MAIN_ICON_COLORS } from 'assets/components';
import { CryptodraftContext } from '../../../../contexts/card/cryptodraft/context';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatDecimalNumber, formatNumber } from '../../../../utilities/formatHelpers';
import { validateNumbers } from '../../../../utilities/formValidation';
import { showAlert } from '../../../../utilities/notifications';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import { WalletsTypes } from '../../constants';
import { MXP_FC_WITHDRAW_COLLATERAL } from '../../mixpanel-events';
import { CARD_CRYPTODRAFT_REQUEST_WITHDRAWAL } from '../../mutations';
import { CARD_CRYPTODRAFT_WITHDRAWABLE, CARD_LINKED_WALLETS } from '../../queries';
import { cryptodraftScreens, forceUpdateOptions } from '../constants';
import MainBodyLayout from '../partials/MainBodyLayout';
import { ICONS } from '../../../../components/Icon/constants';
import Icon from '../../../../components/Icon';

const CryptodraftWithdrawCollateral = () => {
  const { t: translate } = useTranslation();

  const { state, forceUpdateAction, openScreen } = useContext(CryptodraftContext);

  const history = useHistory();

  const [withdrawalAmount, setWithdrawalAmount] = useState(0);
  const [withdrawalFiatAmount, setWithdrawalFiatAmount] = useState(0);
  const [newCollateralAmount, setNewCollateralAmount] = useState(0);
  const [manualInputWidth, setManualInputWidth] = useState(0);
  const [fiatManualInputWidth, setFiatManualInputWidth] = useState(0);
  const [isSwap, setIsSwap] = useState(false);

  const cryptodraftDetails = state?.cryptodraftDetails;
  const collateralCurrency = cryptodraftDetails?.collateral_currency?.toUpperCase();
  const currentLtv = cryptodraftDetails?.ltv_percentage;
  const fiatCurrency = state?.fiatCurrency;
  const screenOptions = state?.screenOptions;

  const { refetch: getLinkedWallet } = useQuery(CARD_LINKED_WALLETS, {
    enabled: false,
  });
  const { data: withdrawable } = useQuery(CARD_CRYPTODRAFT_WITHDRAWABLE, { cacheTime: 0 });

  const { mutate: requestWithdrawal, isLoading } = useMutation(
    CARD_CRYPTODRAFT_REQUEST_WITHDRAWAL,
    {
      onSettled: (_, error) => {
        sendStats(MXP_FC_WITHDRAW_COLLATERAL, {}, FLOWS.FC);
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          forceUpdateAction(forceUpdateOptions.DETAILS, true);
          getLinkedWallet();
          showAlert({
            message: translate('cryptodraft_collateral_withdrawal_success_message'),
            type: 'success',
          });
          history.push('/dashboard/card/details');
        }
      },
    }
  );

  const manualInputSpan = useRef();
  const fiatManualInputSpan = useRef();

  const rate = useRate(cryptodraftDetails?.collateral_currency, fiatCurrency);

  const withdrawableAmount = withdrawable?.withdrawable_amount || 0;
  const pendingWithdrawalAmount = withdrawable?.pending_withdrawal_amount || 0;
  const withdrawalAmountInPrincipal = formatNumber(withdrawalAmount * rate, exchange.FIAT_VALUE);
  const canWithdraw =
    withdrawalAmount && withdrawalAmount > 0 && withdrawalAmount <= withdrawableAmount;

  const principalNetAmount = useMemo(
    () =>
      cryptodraftDetails &&
      formatNumber(
        cryptodraftDetails?.principal_net_amount * currencies[fiatCurrency].conversion,
        exchange.FIAT_VALUE
      ),
    [cryptodraftDetails, fiatCurrency]
  );

  const collateralAmount = useMemo(
    () =>
      cryptodraftDetails &&
      formatNumber(
        cryptodraftDetails?.collateral_amount * currencies[collateralCurrency].conversion -
          pendingWithdrawalAmount,
        exchange.CRYPTO_VALUE
      ),
    [cryptodraftDetails, collateralCurrency, pendingWithdrawalAmount]
  );

  const newCollateralAmountInPrincipal = useMemo(
    () => rate && formatNumber(newCollateralAmount * rate, exchange.FIAT_VALUE),
    [newCollateralAmount, rate]
  );

  const newLtv = useMemo(
    () =>
      principalNetAmount &&
      newCollateralAmountInPrincipal &&
      formatNumber(
        (principalNetAmount / newCollateralAmountInPrincipal) * 100,
        exchange.FIAT_VALUE
      ),
    [principalNetAmount, newCollateralAmountInPrincipal]
  );

  const withdrawableNote = translate('cryptodraft_collateral_withdrawal_withdrawable_note').replace(
    '__AMOUNT__',
    `${withdrawableAmount} ${collateralCurrency}`
  );

  useEffect(() => {
    const amount = collateralAmount - withdrawalAmount;
    const tempNewCollateralAmount = formatNumber(amount, exchange.CRYPTO_VALUE);
    setNewCollateralAmount(tempNewCollateralAmount);
  }, [collateralAmount, withdrawalAmount, collateralCurrency]);

  useEffect(() => {
    const charactersLength =
      fiatManualInputSpan.current.value.length > 1 ? fiatManualInputSpan.current.value.length : 1;
    setFiatManualInputWidth(charactersLength);
  }, [withdrawalFiatAmount, isSwap]);

  useEffect(() => {
    const charactersLength =
      manualInputSpan.current.value.length > 1 ? manualInputSpan.current.value.length : 2;

    setManualInputWidth(charactersLength);
  }, [withdrawalAmount, isSwap]);

  const handleWithdrawalAmountChange = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(formattedAmount, WalletsTypes.CRYPTO)) {
      return;
    }

    if (amount > withdrawableAmount) {
      setWithdrawalFiatAmount(withdrawalAmountInPrincipal);
      setWithdrawalAmount(withdrawableAmount);
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setWithdrawalFiatAmount(0);
      setWithdrawalAmount(0);
    }

    const calcFiat = formatNumber(decimalAmount * rate, exchange.FIAT_VALUE);
    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    setWithdrawalFiatAmount(formattedFiatCalc.formattedAmount);
    setWithdrawalAmount(formattedAmount);
  };

  const handleChangeFiatAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(formattedAmount, WalletsTypes.FIAT)) {
      return;
    }

    if (amount > withdrawableAmount * rate) {
      setWithdrawalFiatAmount(formatNumber(withdrawableAmount * rate, exchange.FIAT_VALUE));
      setWithdrawalAmount(withdrawableAmount);
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setWithdrawalFiatAmount(0);
      setWithdrawalAmount(0);
    }

    const calcCrypto = formatNumber(decimalAmount / rate, exchange.CRYPTO_VALUE);
    const formattedCalcCrypto = formatDecimalNumber(calcCrypto);

    setWithdrawalAmount(formattedCalcCrypto.formattedAmount);
    setWithdrawalFiatAmount(formattedAmount);
  };

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  return (
    <MainBodyLayout
      withBack={screenOptions?.openedFromDetails}
      onPrevClick={() => openScreen(cryptodraftScreens.DETAILS)}
      withInfoIcon
      title={translate('cryptodraft_collateral_withdrawal_title')}>
      <p className="mt-5">{withdrawableNote}</p>
      <div className="mt-5">
        <p className="text-shadow text-f6 p-0">
          {translate('cryptodraft_withdraw_select_amount_label')}
        </p>
      </div>
      <div className="mt-2">
        <FocusableInputArea className="mb-1">
          <input
            min={0}
            required
            type="text"
            className="form-control bg-transparent p-0 text-dark text-center border-0"
            value={isSwap ? withdrawalAmount : withdrawalFiatAmount}
            style={{
              fontSize: '1rem',
              width: `${isSwap ? manualInputWidth : fiatManualInputWidth + 1}ch`,
              height: '3rem',
            }}
            ref={isSwap ? manualInputSpan : fiatManualInputSpan}
            onChange={(e) => (isSwap ? handleWithdrawalAmountChange(e) : handleChangeFiatAmount(e))}
            placeholder="0 "
          />
          <span
            style={{ fontSize: 'inherit', pointerEvents: 'none' }}
            className="text-f5 text-dark">
            {isSwap ? collateralCurrency?.toUpperCase() : fiatCurrency?.toUpperCase()}
          </span>
          <span
            style={{ width: '1rem', height: '1rem' }}
            role="button"
            className="d-flex justify-content-center align-items-center ps-1"
            tabIndex="0"
            onKeyPress={() => setIsSwap((prev) => !prev)}
            onClick={() => setIsSwap((prev) => !prev)}>
            <Icon icon={ICONS.SWITCH} color={MAIN_ICON_COLORS.PRIMARY} />
          </span>
        </FocusableInputArea>
        <FocusableInputArea className="h1 amount-input">
          <input
            required
            min="0"
            type="text"
            className="form-control bg-transparent text-bold p-0 text-center border-0"
            value={isSwap ? withdrawalFiatAmount : withdrawalAmount}
            style={{
              fontSize: 'inherit',
              width: `${manualInputWidth + 1}ch`,
              color: 'inherit',
              height: 'inherit',
              textIndent: manualInputWidth || fiatManualInputWidth ? 0 : '34%',
            }}
            ref={isSwap ? fiatManualInputSpan : manualInputSpan}
            onChange={(e) => (isSwap ? handleChangeFiatAmount(e) : handleWithdrawalAmountChange(e))}
            onFocus={handleFocus}
            onBlur={handleBlurAmount}
            placeholder="0  "
          />
          <b
            style={{
              fontSize: 'inherit',
              pointerEvents: 'none',
              color: 'inherit',
            }}>
            {isSwap ? fiatCurrency?.toUpperCase() : collateralCurrency?.toUpperCase()}
          </b>
        </FocusableInputArea>
        <div className="mb-4 position-relative">
          <div className="slider-progress">
            <div className="progress">
              <div
                className="progress-bar"
                style={{
                  width: `${(withdrawalAmount / withdrawableAmount) * 100}%`,
                }}
              />
            </div>
            <input
              type="range"
              className="form-range"
              value={withdrawalAmount}
              max={withdrawableAmount}
              min={0}
              step={0.00000001}
              onChange={(e) => handleWithdrawalAmountChange(e, true)}
            />
          </div>
          {/* this lines for implementing edges for the slider */}
          <div
            className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start"
            style={{ top: '15%' }}>
            {Array.from({ length: 7 }, (e, i) => i + 1).map((item) => (
              <hr
                key={item}
                className={`progress-edges text-disabled ${
                  item === 1 || item === 7 ? 'progress-main-edge' : 'progress-edges'
                }`}
                style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
              />
            ))}
            {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
              <hr
                key={item}
                className={`progress-edges text-disabled ${
                  item === 6 ? 'progress-main-edge' : 'progress-edges'
                }`}
                style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
              />
            ))}
            {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
              <hr
                key={item}
                className={`progress-edges text-disabled ${
                  item === 6 ? 'progress-main-edge' : 'progress-edges'
                }`}
                style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
              />
            ))}
            {Array.from({ length: 6 }, (e, i) => i + 1).map((item) => (
              <hr
                key={item}
                className={`progress-edges text-disabled ${
                  item === 6 ? 'progress-main-edge' : 'progress-edges'
                }`}
                style={{ transform: 'rotate(90deg)', height: '1rem !important' }}
              />
            ))}
          </div>
          <div className="position-absolute w-100 d-flex flex-row justify-content-between align-items-start">
            <p className="text-f6 text-disabled">{`0 ${collateralCurrency}`}</p>
            <p className="text-f6 text-disabled">{`${withdrawableAmount} ${collateralCurrency}`}</p>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <p className="text-shadow text-f6 p-0">{translate('interest_rate_label')}</p>
      </div>
      <div className="rounded-extra bg-lighter mb-3 mt-2">
        <div className="d-flex flex-column p-0">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="text-dark text-f6">
                {translate('cryptodraft_collateral_withdrawal_current_LTV')}
              </p>
              <p className="text-f6">{`${currentLtv} %`}</p>
            </div>
          </div>
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <p className="text-dark text-f6">
                {translate('cryptodraft_collateral_withdrawal_new_LTV')}
              </p>
              <p className="text-f6">{`${newLtv} %`}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex flex-row mt-1 mb-4 rounded-extra bg-lighter p-3">
        <p className="text-dark text-f6 d-flex align-items-center">
          {translate('cryptodraft_collateral_withdrawal_new_collateral_balance')}
        </p>
        <span className="text-right flex-fill">
          <p className="text-f6 m-0">{`${newCollateralAmount} ${collateralCurrency}`}</p>
          <p className="text-shadow text-f6 m-0">{`${newCollateralAmountInPrincipal} ${fiatCurrency.toUpperCase()}`}</p>
        </span>
      </div>
      <div className="btn-container">
        <button
          loading={isLoading ? 'true' : 'false'}
          disabled={!canWithdraw}
          type="button"
          className="btn py-3 btn-primary w-100"
          onClick={() => {
            requestWithdrawal({ withdrawal_amount: withdrawalAmount });
          }}>
          {translate('cryptodraft_collateral_withdrawal_complete_withdrawal')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default CryptodraftWithdrawCollateral;
